import React, { memo, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import type { TableBlock } from '../../../types/backend';
import BlockShell from './BlockShell';

interface Props {
  block: TableBlock;
}

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default' | 'info'> = {
  running: 'success',
  active: 'success',
  healthy: 'success',
  available: 'success',
  enabled: 'success',
  ok: 'success',
  passed: 'success',
  resolved: 'success',
  stopped: 'default',
  disabled: 'default',
  terminated: 'default',
  idle: 'default',
  inactive: 'default',
  pending: 'info',
  starting: 'info',
  updating: 'info',
  initializing: 'info',
  degraded: 'warning',
  warning: 'warning',
  impaired: 'warning',
  failed: 'error',
  error: 'error',
  critical: 'error',
  unhealthy: 'error',
  unreachable: 'error',
};

function cellDisplay(value: string): React.ReactNode {
  if (value === '') return '—';
  const color = STATUS_COLORS[value.toLowerCase()];
  if (color) {
    return (
      <Chip
        label={value}
        color={color}
        size="small"
        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
      />
    );
  }
  return value;
}

type SortDir = 'asc' | 'desc';

const TableBlockView: React.FC<Props> = memo(({ block }) => {
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = useCallback((ci: number) => {
    setSortCol((prev) => {
      if (prev === ci) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return ci;
      }
      setSortDir('asc');
      return ci;
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (sortCol === null) return block.rows;
    return [...block.rows].sort((a, b) => {
      const cmp = String(a[sortCol] ?? '').localeCompare(String(b[sortCol] ?? ''), undefined, {
        numeric: true,
      });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [block.rows, sortCol, sortDir]);

  return (
    <BlockShell bodyPadding={0}>
      <TableContainer sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader aria-label="data table">
          <TableHead>
            <TableRow>
              {block.columns.map((col, i) => (
                <TableCell
                  key={i}
                  sortDirection={sortCol === i ? sortDir : false}
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap', bgcolor: 'background.paper' }}
                >
                  <TableSortLabel
                    active={sortCol === i}
                    direction={sortCol === i ? sortDir : 'asc'}
                    onClick={() => handleSort(i)}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={block.columns.length}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    No data
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, ri) => (
                <TableRow key={ri} hover>
                  {row.map((cell, ci) => {
                    const isId = ci === 0;
                    return (
                      <TableCell
                        key={ci}
                        sx={{
                          whiteSpace: 'nowrap',
                          fontWeight: isId ? 500 : 400,
                          fontFamily: isId ? 'monospace' : undefined,
                          fontSize: isId ? '0.78rem' : undefined,
                        }}
                      >
                        {cellDisplay(cell)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {sortedRows.length > 0 && (
        <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {sortedRows.length} {sortedRows.length === 1 ? 'row' : 'rows'}
          </Typography>
        </Box>
      )}
    </BlockShell>
  );
});

TableBlockView.displayName = 'TableBlockView';
export default TableBlockView;
